import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * DemoPage component
 * Comprehensive trial offer for healthcare SaaS claims-processing platform
 * Smart routing: redirects authenticated users to their current trial step
 */
const DemoPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isCheckingProgress, setIsCheckingProgress] = useState(false);

  // Check trial progress and redirect authenticated users to the appropriate step
  useEffect(() => {
    const checkTrialProgress = async () => {
      if (!isAuthenticated) {
        return; // Unauthenticated users stay on the demo page
      }

      setIsCheckingProgress(true);
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';
        const response = await fetch(`${API_URL}/trial/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const trialData = data.data;

          // Route based on trial_current_step
          if (trialData && trialData.trial_status === 'active') {
            const currentStep = trialData.trial_current_step;

            if (currentStep === 1) {
              // Step 1 not completed yet, go to step 1
              navigate('/cdi-trial/step-1');
            } else if (currentStep === 2) {
              // Step 1 completed, on step 2
              navigate('/cdi-trial/step-2');
            } else if (currentStep === 3) {
              // Step 2 completed, on step 3
              navigate('/cdi-trial/step-3');
            } else if (currentStep > 3 || trialData.trial_status === 'completed') {
              // All steps completed, go to dashboard
              navigate('/dashboard');
            }
          }
          // If no active trial, stay on demo page to allow them to start
        }
      } catch (error) {
        console.error('Error checking trial progress:', error);
        // On error, stay on demo page
      } finally {
        setIsCheckingProgress(false);
      }
    };

    checkTrialProgress();
  }, [isAuthenticated, navigate]);

  // Executive Objective & Success Criteria
  const trialObjective = {
    goal: "Demonstrate the effectiveness of Claim Denial Insights (CDI) to significantly increase clean claim rate (CCR) and reduce A/R days through real-time claims processing",
    upgradeInfo: [
      "60-day paid trial",
      "AI Model training – for 5 payors",
      "3000 approved claims by payor (basic formatted claims – not pathology, radiology - requires full version)",
      "500-1000 claims that have been rejected by payor",
      "Daily submission of 1000 claims per payor",
      "Daily report generated for the 1000 claims per payor outlining suggestions to correct the claims"
    ],
    successMetrics: [
      "85%+ Trial Activation Rate (complete upload & processing)",
      "Measurable improvement in Clean Claim Rate (CCR)",
      "Reduction in A/R days through denial prevention"
    ],
    trialLength: "30 days",
    cdiComplete: [
      "Multi-payor AI optimization across all major insurance providers",
      "Advanced pathology and radiology claims processing capabilities", 
      "Enterprise-grade scalability with unlimited daily claim submissions",
      "Real-time API integration with existing EMR and billing systems",
      "Comprehensive analytics dashboard with predictive denial insights",
      "Custom reporting and automated workflow optimization"
    ]
  };

  const demoSteps = [
    {
      id: 1,
      title: "Create your account and workspace",
      description: "Sign up with email/SSO and set up your organization",
      duration: "4 minutes",
      details: [
        "Sign up with email/SSO and verify your email",
        "Accept Terms and Privacy Policy",
        "Acknowledge: trial accepts de-identified data only (no PHI/PII)",
        "Enter your organization name"
      ]
    },
    {
      id: 2,
      title: "Upload your claims data",
      description: "Prepare and upload your claims file with automated processing",
      duration: "10 minutes",
      details: [
        "Prepare claims zip with CSV or Parquet format",
        "Drag-and-drop the .zip file and start upload",
        "Videxa runs virus scan and performs anonymization if not supplied in that manner",
        "Preview anonymized results",
        "Fix any flagged issues and re-upload if needed"
      ]
    },
    {
      id: 3,
      title: "Validate, review, export, share, and plan next steps",
      description: "Review validation results, export reports, share with team, and manage trial completion",
      duration: "15 minutes",
      details: [
        "Review validation results and status summary",
        "See totals by status: Approved, Rejected, Denied, Unprocessed",
        "View top reasons for Rejected/Denied and data quality notes",
        "Download CSV summaries and executive DOCX reports",
        "Share results via email with team members",
        "Select data retention preferences",
        "Choose a plan for conversion when ready"
      ]
    }
  ];

  const toggleStepCompletion = (stepId) => {
    setCompletedSteps(prev => 
      prev.includes(stepId) 
        ? prev.filter(id => id !== stepId)
        : [...prev, stepId]
    );
  };

  const totalDuration = demoSteps.reduce((total, step) => {
    const minutes = parseInt(step.duration);
    return total + minutes;
  }, 0);

  // Show loading state while checking progress for authenticated users
  if (isAuthenticated && isCheckingProgress) {
    return (
      <div className="home-page min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking your trial progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Claim Denial Insights <span style={{ color: '#FFC000' }}>Trial</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Welcome to the Videxa trial as a 30-day trial of Claim Denial Insights (CDI) that will be applied to your real
              time claims processing activities. Our goal is to demonstrate the effectiveness of this solution to significantly increase your
              clean claim rate (CCR) and reduce A/R days.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link 
                to="/cdi-trial/step-1"
                style={{ 
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                className="hover:bg-opacity-90"
              >
                Start Trial
              </Link>
              <Link
                to="/contact"
                style={{ 
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                className="hover:bg-[#0E3056] hover:text-white"
              >
                Contact Us
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="text-3xl font-bold mb-2" style={{ color: '#FFC000' }}>30 Days</div>
                <div className="text-sm text-gray-700">CDI Trial Period</div>
                <div className="text-xs text-gray-600 mt-2">Full evaluation period</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="text-3xl font-bold mb-2" style={{ color: '#FFC000' }}>100</div>
                <div className="text-sm text-gray-700">Daily Claims Submission</div>
                <div className="text-xs text-gray-600 mt-2">Real-time processing</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="text-3xl font-bold mb-2" style={{ color: '#FFC000' }}>3000+</div>
                <div className="text-sm text-gray-700">AI Model Training Claims</div>
                <div className="text-xs text-gray-600 mt-2">Single payor optimization</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Objectives Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
              Trial Objectives & Success Framework
            </h2>
            
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  🎯 Trial Goal
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {trialObjective.goal}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  🏥 Upgrade to 60-day paid trial
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  {trialObjective.upgradeInfo.map((info, idx) => (
                    <li key={idx} className="flex items-center">
                      <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                      {info}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  🛡️ CDI Complete Product Offering
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  {trialObjective.cdiComplete.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Content */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              
              {/* Progress Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                    Trial Progress
                  </h2>
                  <div className="space-y-4">
                    {demoSteps.map((step) => (
                      <div key={step.id} className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleStepCompletion(step.id)}
                          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                            completedSteps.includes(step.id)
                              ? 'border-blue-500 bg-green-500 text-white'
                              : 'border-blue-500 bg-transparent text-blue-500 hover:bg-blue-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {completedSteps.includes(step.id) && (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        <span className={`text-sm ${
                          completedSteps.includes(step.id)
                            ? 'text-green-600 dark:text-green-400 font-medium'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {step.title}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Progress: {completedSteps.length}/{demoSteps.length}
                    </h3>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(completedSteps.length / demoSteps.length) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Demo Steps Content */}
              <div className="lg:col-span-3">
                <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
                  End-User Trial Walkthrough
                </h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-8 border-l-4 border-blue-500">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-4">
                    CDI Trial Implementation Goals
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody className="text-blue-800 dark:text-blue-200">
                        <tr className="border-b border-blue-200/30 dark:border-blue-700/30">
                          <td className="py-3 pr-4 font-medium whitespace-nowrap">Primary Objective:</td>
                          <td className="py-3">Demonstrate measurable improvement in Clean Claim Rate (CCR) and reduction in A/R days through AI-powered denial insights</td>
                        </tr>
                        <tr className="border-b border-blue-200/30 dark:border-blue-700/30">
                          <td className="py-3 pr-4 font-medium whitespace-nowrap">Success Indicators:</td>
                          <td className="py-3">Quantifiable CCR improvement, faster claim processing, reduced manual intervention, and actionable denial prevention insights</td>
                        </tr>
                        <tr className="border-b border-blue-200/30 dark:border-blue-700/30">
                          <td className="py-3 pr-4 font-medium whitespace-nowrap">Real-time Impact:</td>
                          <td className="py-3">Daily processing of 100 claims with immediate feedback, AI learning from 3000+ training claims, and continuous model optimization</td>
                        </tr>
                        <tr>
                          <td className="py-3 pr-4 font-medium whitespace-nowrap">Focus Area:</td>
                          <td className="py-3">Single payor optimization to maximize learning efficiency and demonstrate targeted improvements in claim acceptance rates</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="space-y-6">
                  {demoSteps.map((step) => (
                    <div 
                      key={step.id} 
                      className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border-l-4 transition-colors ${
                        completedSteps.includes(step.id)
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-blue-500'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Step {step.id}: {step.title}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300">
                            {step.description}
                          </p>
                        </div>
                        <div className="text-right">
                          <a 
                            href={`/cdi-trial/step-${step.id}`}
                            className="bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 transition-colors inline-block"
                          >
                            {step.title.split(' ')[0]}
                          </a>
                          {completedSteps.includes(step.id) && (
                            <div className="text-green-600 dark:text-green-400 text-sm font-medium mt-1">
                              ✓ Completed
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                          What we'll cover:
                        </h4>
                        <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                          {step.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Implementation Readiness */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
              Implementation Readiness Checklist
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="text-3xl mb-4">🔒</div>
                <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Security & Compliance</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">HIPAA, SOC 2, audit trails, encryption</p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="text-3xl mb-4">🏗️</div>
                <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Architecture</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Multi-tenant, scalable, high availability</p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="text-3xl mb-4">🔄</div>
                <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Operations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Monitoring, support, incident response</p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="text-3xl mb-4">📈</div>
                <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Go-to-Market</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Sales enablement, conversion metrics</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
            Ready to Start Your CDI Trial?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Experience Claim Denial Insights (CDI) with your real-time claims processing activities. 
            Our 30-day trial will demonstrate measurable improvements to your clean claim rate (CCR) 
            and significant reduction in A/R days.
          </p>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-auto mb-8 shadow-sm">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Trial includes:</div>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              <li>✓ Daily processing of 100 claims</li>
              <li>✓ AI model training with 3000+ claims</li>
              <li>✓ Real-time denial insights and corrections</li>
              <li>✓ Daily reports with improvement suggestions</li>
              <li>✓ Clean claim rate (CCR) improvement tracking</li>
            </ul>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
            >
              🚀 Start CDI Trial
            </a>
            <a 
              href="/contact" 
              className="border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
            >
              📞 Schedule Consultation
            </a>
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-6">
            Real-time claims processing • 30-day trial • HIPAA-compliant • Single payor focus
          </p>
        </div>
      </section>
    </div>
  );
};

export default DemoPage;
